import React, { useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { useGetAllMapNodesQuery, useContactNodeMutation, useUncontactNodeMutation } from '../store/api/mapNodesApi';
import AddNodeModal from './AddNodeModal';
import MapClickHandler from './MapClickHandler';
import Snackbar from './Snackbar';
import NodesSidebar from './NodesSidebar';
import SignalPropagation from './SignalPropagation';
import '../styles/Map.css';

// Component to handle map zoom from outside
const MapZoomController = ({ center, zoom }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (center && zoom) {
      map.flyTo(center, zoom, {
        duration: 1.5,
        easeLinearity: 0.5
      });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Custom red marker icon
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const Map = () => {
  const { isAuthenticated, login, user } = useAuth();
  const [hideContacted, setHideContacted] = useState(false);
  const [sortBy, setSortBy] = useState('contacts');
  const [showAddModal, setShowAddModal] = useState(false);
  const [mapClickCallback, setMapClickCallback] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [mapTheme, setMapTheme] = useState('dark'); // 'dark' or 'light'
  
  // Initialize sidebar based on screen size
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return window.innerWidth > 768; // Open on desktop, closed on mobile
  });
  
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [legendExpanded, setLegendExpanded] = useState(false);
  
  // Use RTK Query to fetch nodes
  const { data: allNodes = [] } = useGetAllMapNodesQuery({
    hideContacted,
    sortBy: 'contacts', // Always fetch all, we'll sort client-side
  });
  
  // Client-side sorting based on selected option
  const nodes = React.useMemo(() => {
    let sorted = [...allNodes];
    
    if (sortBy === 'contacts') {
      sorted = sorted.sort((a, b) => (b.contactCount || 0) - (a.contactCount || 0));
    } else if (sortBy === 'date') {
      sorted = sorted.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
    } else if (sortBy === 'mynodes' && isAuthenticated) {
      const userId = user?.id || user?.Id;
      // Put user's nodes first, then sort by contacts
      sorted = sorted.sort((a, b) => {
        const aIsMine = a.addedByDiscordUserId === userId;
        const bIsMine = b.addedByDiscordUserId === userId;
        
        if (aIsMine && !bIsMine) return -1;
        if (!aIsMine && bIsMine) return 1;
        
        // If both mine or both not mine, sort by contacts
        return (b.contactCount || 0) - (a.contactCount || 0);
      });
    }
    
    return sorted;
  }, [allNodes, sortBy, isAuthenticated, user]);
  
  const [contactNode, { isLoading: isContacting }] = useContactNodeMutation();
  const [uncontactNode, { isLoading: isUncontacting }] = useUncontactNodeMutation();
  
  const showSnackbar = (message, type = 'info') => {
    setSnackbar({ message, type });
  };
  
  // Arizona coordinates
  const center = [34.0489, -111.0937];
  const zoom = 7;

  const handleContact = async (nodeId, node) => {
    if (!isAuthenticated) {
      login();
      return;
    }

    // Check if user has already contacted this node
    // Backend returns capitalized property names (C# convention)
    const userId = user?.id || user?.Id;
    const userContact = node.contacts?.find(c => c.discordUserId === userId);
    const hasContacted = !!userContact;

    try {
      if (hasContacted) {
        // Remove contact (undo)
        await uncontactNode(nodeId).unwrap();
        showSnackbar('Contact removed', 'info');
      } else {
        // Add contact
        await contactNode(nodeId).unwrap();
        showSnackbar('Contact recorded successfully!', 'success');
      }
    } catch (error) {
      console.error('Error managing contact:', error);
      
      // Handle different error types
      let errorMsg = 'An error occurred';
      if (error?.data) {
        // Text response
        errorMsg = typeof error.data === 'string' ? error.data : error.data.message || error.data.title;
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleAddNode = () => {
    if (!isAuthenticated) {
      login();
      return;
    }
    setShowAddModal(true);
  };

  const handleNodeAdded = () => {
    setShowAddModal(false);
    setMapClickCallback(null);
    // RTK Query will automatically refetch due to cache invalidation
  };

  const handleMapClickRequest = (callback) => {
    setMapClickCallback(() => callback);
  };

  const handleMapClick = (lat, lng) => {
    if (mapClickCallback) {
      setPendingLocation({ lat, lng });
    }
  };

  const handleConfirmLocation = () => {
    if (pendingLocation && mapClickCallback) {
      mapClickCallback(pendingLocation.lat, pendingLocation.lng);
      setMapClickCallback(null);
      setPendingLocation(null);
    } else {
      // Clear pending location even if callback is missing
      setPendingLocation(null);
    }
  };

  const handleCancelLocation = () => {
    setMapClickCallback(null);
    setPendingLocation(null);
  };

  const handleCopyNodeId = (nodeId) => {
    // Copy with meshcore:// prefix for easy sharing
    const fullNodeId = `meshcore://${nodeId}`;
    navigator.clipboard.writeText(fullNodeId).then(() => {
      showSnackbar('Node ID copied to clipboard!', 'success');
    }).catch(() => {
      showSnackbar('Failed to copy to clipboard', 'error');
    });
  };

  const handleNodeClickFromSidebar = (lat, lng) => {
    setMapCenter([lat, lng]);
    setMapZoom(14); // Zoom in when clicking a node
    
    // Close sidebar on mobile after clicking a node
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const toggleMapTheme = () => {
    setMapTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="map-container">
      <NodesSidebar 
        isOpen={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onNodeClick={handleNodeClickFromSidebar}
        sortedNodes={nodes}
        sortBy={sortBy}
        onMapClickRequest={handleMapClickRequest}
      />
      
      <div className="box m-3">
        <div className="field is-horizontal">
          <div className="field-body">
            <div className="field">
              <div className="control">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={hideContacted}
                    onChange={(e) => setHideContacted(e.target.checked)}
                    disabled={!isAuthenticated}
                  />
                  {' '}Hide Contacted Nodes
                </label>
              </div>
            </div>
            
            <div className="field has-addons">
              <p className="control">
                <span className="button is-static">Sort by:</span>
              </p>
              <p className="control">
                <span className="select">
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="contacts">Most Contacts</option>
                    <option value="date">Most Recent</option>
                    {isAuthenticated && <option value="mynodes">My Nodes First</option>}
                  </select>
                </span>
              </p>
            </div>

            {isAuthenticated && (
              <div className="field">
                <div className="control">
                  <button className="button is-primary" onClick={handleAddNode}>
                    <span className="icon">
                      <i className="fas fa-plus"></i>
                    </span>
                    <span>Add Node</span>
                  </button>
                </div>
              </div>
            )}

            <div className="field">
              <div className="control">
                <button 
                  className="button is-info" 
                  onClick={toggleMapTheme}
                  title={`Switch to ${mapTheme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  <span className="icon">
                    <i className={`fas fa-${mapTheme === 'dark' ? 'sun' : 'moon'}`}></i>
                  </span>
                  <span>{mapTheme === 'dark' ? 'Light' : 'Dark'} Mode</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="map-wrapper" style={{ position: 'relative' }} data-theme={mapTheme}>
        {mapClickCallback && (
          <div className="map-click-overlay">
            <div className="notification is-info is-light">
              <div className="content has-text-centered">
                <p className="has-text-weight-bold">
                  <span className="icon">
                    <i className="fas fa-crosshairs"></i>
                  </span>
                  Click anywhere on the map to set the node location
                </p>
              </div>
            </div>
          </div>
        )}
        <MapContainer 
          center={center} 
          zoom={zoom} 
          style={{ 
            height: '100%', 
            width: '100%', 
            cursor: mapClickCallback ? 'crosshair' : 'grab',
            position: 'relative',
            zIndex: 1
          }}
        >
          <TileLayer
            key={mapTheme} // Force re-render when theme changes
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | &copy; <a href="https://carto.com/">CARTO</a>'
            url={mapTheme === 'dark' 
              ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            }
          />
          
          <MapZoomController center={mapCenter} zoom={mapZoom} />
          <MapClickHandler onMapClick={handleMapClick} isActive={!!mapClickCallback} />
          
          {/* Signal propagation overlay */}
          {hoveredNode && (
            <SignalPropagation
              nodeId={hoveredNode.nodeId}
              latitude={hoveredNode.latitude}
              longitude={hoveredNode.longitude}
              show={true}
            />
          )}
          
          {/* Pending location marker */}
          {pendingLocation && (
            <Marker 
              position={[pendingLocation.lat, pendingLocation.lng]}
              icon={redIcon}
            >
              <Tooltip direction="top" offset={[0, -40]} opacity={0.9} permanent className="selected-location-tooltip">
                <div>
                  <strong>Selected Location</strong><br/>
                  Lat: {pendingLocation.lat.toFixed(6)}<br/>
                  Lng: {pendingLocation.lng.toFixed(6)}
                </div>
              </Tooltip>
            </Marker>
          )}
          
          {nodes.map((node, index) => {
            if (!node.latitude || !node.longitude) return null;
            
            // Backend returns capitalized property names (C# convention)
            const userId = user?.id || user?.Id;
            const userContact = node.contacts?.find(c => c.discordUserId === userId);
            const hasContacted = !!userContact;
            const isProcessing = isContacting || isUncontacting;
            
            // Calculate z-index based on sort order (higher index = higher priority = on top)
            const zIndexOffset = nodes.length - index;
            
            return (
              <Marker 
                key={node.nodeId} 
                position={[node.latitude, node.longitude]}
                icon={redIcon}
                zIndexOffset={zIndexOffset}
                eventHandlers={{
                  mouseover: () => setHoveredNode(node),
                  mouseout: () => setHoveredNode(null),
                }}
              >
                <Tooltip direction="top" offset={[0, -40]} opacity={0.9} permanent={false}>
                  {node.friendlyName || node.nodeHash}
                </Tooltip>
                <Popup>
                  <div className="node-popup">
                    <h4 className="title is-5 has-text-primary mb-2">{node.friendlyName || node.nodeHash}</h4>
                    <p className="has-text-grey is-italic mb-1">Added by: {node.addedByDiscordUsername}</p>
                    <p className="has-text-weight-semibold mb-1">Contacts: {node.contactCount || 0}</p>
                    <p className="has-text-grey is-size-7 mb-2">
                      Added: {new Date(node.addedAt).toLocaleDateString()}
                    </p>
                    <div className="buttons">
                      <button 
                        className={`button is-small ${hasContacted ? 'is-grey' : 'is-success'}`}
                        onClick={() => handleContact(node.nodeId, node)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Processing...' : hasContacted ? '✓ Contacted' : 'I Contacted This Node'}
                      </button>
                      <button 
                        className="button is-small is-primary"
                        onClick={() => handleCopyNodeId(node.nodeId)}
                        title="Copy Node ID"
                      >
                        <span className="icon is-small">
                          <i className="fas fa-copy"></i>
                        </span>
                        <span>Copy ID</span>
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        
        {/* Location confirmation overlay */}
        {pendingLocation && (
          <div className="location-confirmation-overlay">
            <div className="buttons">
              <button 
                className="button is-danger is-medium"
                onClick={handleCancelLocation}
              >
                <span className="icon">
                  <i className="fas fa-times"></i>
                </span>
                <span>Cancel</span>
              </button>
              <button 
                className="button is-success is-medium"
                onClick={handleConfirmLocation}
              >
                <span className="icon">
                  <i className="fas fa-check"></i>
                </span>
                <span>Confirm Location</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddNodeModal
          onClose={() => {
            setShowAddModal(false);
            setMapClickCallback(null);
          }}
          onNodeAdded={handleNodeAdded}
          onMapClickRequest={handleMapClickRequest}
        />
      )}

      {snackbar && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}

      {/* Signal Strength Legend - Only show for repeater/room server nodes */}
      {hoveredNode && (hoveredNode.isRepeater || hoveredNode.isRoomServer) && (
        <div 
          className={`signal-legend ${legendExpanded ? 'expanded' : 'collapsed'}`}
          onMouseEnter={() => setHoveredNode(hoveredNode)}
          onMouseLeave={(e) => {
            // Don't clear hoveredNode if we're just clicking the legend
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setHoveredNode(null);
              setLegendExpanded(false);
            }
          }}
        >
          <div className="legend-title" onClick={() => setLegendExpanded(!legendExpanded)}>
            <span>Signal Coverage (LoRa 908 MHz SF11)</span>
            <span className="legend-toggle">
              <i className={`fas fa-chevron-${legendExpanded ? 'down' : 'up'}`}></i>
            </span>
          </div>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#48c774' }}></span>
              <span>Strong (&gt;70%) • -80 to -100 dBm</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#00d1b2' }}></span>
              <span>Good (50-70%) • -100 to -115 dBm</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#ffdd57' }}></span>
              <span>Weak (30-50%) • -115 to -125 dBm</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#ff3860' }}></span>
              <span>Very Weak (&lt;30%) • -125 to -134 dBm</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Map;

